import React, { useMemo, useState, Children } from 'react';
import { ChevronRightIcon, ChevronDownIcon, BuildingIcon, LayersIcon, FolderIcon } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import type { SubOrganization, Group, SubGroup } from '../schemas/types';
interface HierarchyNode {
  id: string;
  type: 'organization' | 'sub-org' | 'group' | 'sub-group';
  name: string;
  participantCount: number;
  children: HierarchyNode[];
  metadata?: {
    city?: string;
    state?: string;
    gradeLevel?: string;
    description?: string;
  };
}
interface HierarchyVisualizationPanelProps {
  subOrgs: SubOrganization[];
  groups: Group[];
  subGroups: SubGroup[];
  participantCounts: {
    [key: string]: number;
  };
  selectedNodeId?: string;
  onNodeSelect: (nodeId: string, nodeType: string) => void;
}
export function HierarchyVisualizationPanel({
  subOrgs,
  groups,
  subGroups,
  participantCounts,
  selectedNodeId,
  onNodeSelect
}: HierarchyVisualizationPanelProps) {
  const {
    currentOrganization
  } = useOrganization();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set([currentOrganization.id]));
  console.log('[HierarchyPanel] Rendering with data:', {
    subOrgsCount: subOrgs.length,
    groupsCount: groups.length,
    subGroupsCount: subGroups.length,
    participantCountsKeys: Object.keys(participantCounts).length,
    selectedNodeId
  });
  // Build hierarchy tree
  const hierarchyTree = useMemo(() => {
    console.log('[HierarchyPanel] Building hierarchy tree...');
    const isK12 = currentOrganization.orgType === 'K12';
    // Calculate total participants for organization
    const totalParticipants = Object.values(participantCounts).reduce((sum, count) => sum + count, 0);
    console.log('[HierarchyPanel] Total participants:', totalParticipants);
    const rootNode: HierarchyNode = {
      id: currentOrganization.id,
      type: 'organization',
      name: currentOrganization.name,
      participantCount: totalParticipants,
      children: []
    };
    if (isK12) {
      console.log('[HierarchyPanel] Building K12 hierarchy...');
      // K12: Organization → Sub-Orgs (Schools) → Groups (Grades) → Sub-Groups (Classrooms)
      subOrgs.forEach(subOrg => {
        const subOrgGroups = groups.filter(g => g.subOrgId === subOrg.id);
        console.log(`[HierarchyPanel] Sub-org ${subOrg.name}: ${subOrgGroups.length} groups`);
        const subOrgNode: HierarchyNode = {
          id: subOrg.id,
          type: 'sub-org',
          name: subOrg.name,
          participantCount: participantCounts[subOrg.id] || 0,
          metadata: {
            city: subOrg.city,
            state: subOrg.state
          },
          children: []
        };
        subOrgGroups.forEach(group => {
          const groupSubGroups = subGroups.filter(sg => sg.groupId === group.id);
          console.log(`[HierarchyPanel]   Group ${group.name}: ${groupSubGroups.length} sub-groups`);
          const groupNode: HierarchyNode = {
            id: group.id,
            type: 'group',
            name: group.name,
            participantCount: participantCounts[group.id] || 0,
            metadata: {
              gradeLevel: group.metadata?.gradeLevel,
              description: group.metadata?.description
            },
            children: []
          };
          groupSubGroups.forEach(subGroup => {
            console.log(`[HierarchyPanel]     Sub-group ${subGroup.name}`);
            groupNode.children.push({
              id: subGroup.id,
              type: 'sub-group',
              name: subGroup.name,
              participantCount: participantCounts[subGroup.id] || 0,
              metadata: {
                description: subGroup.metadata?.roomNumber ? `Room ${subGroup.metadata.roomNumber}` : undefined
              },
              children: []
            });
          });
          subOrgNode.children.push(groupNode);
        });
        rootNode.children.push(subOrgNode);
      });
    } else {
      console.log('[HierarchyPanel] Building College hierarchy...');
      // College: Organization → Groups (Teams/Classes)
      groups.forEach(group => {
        console.log(`[HierarchyPanel] Group ${group.name}`);
        rootNode.children.push({
          id: group.id,
          type: 'group',
          name: group.name,
          participantCount: participantCounts[group.id] || 0,
          metadata: {
            description: group.metadata?.description
          },
          children: []
        });
      });
    }
    console.log('[HierarchyPanel] Hierarchy tree built:', {
      rootChildren: rootNode.children.length,
      rootNode
    });
    return rootNode;
  }, [currentOrganization, subOrgs, groups, subGroups, participantCounts]);
  const toggleExpand = (nodeId: string) => {
    console.log('[HierarchyPanel] Toggling expand for node:', nodeId);
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };
  const handleNodeClick = (nodeId: string, nodeType: string) => {
    console.log('[HierarchyPanel] Node clicked:', {
      nodeId,
      nodeType
    });
    onNodeSelect(nodeId, nodeType);
  };
  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'organization':
        return <BuildingIcon className="w-4 h-4" data-id="element-201" />;
      case 'sub-org':
        return <BuildingIcon className="w-4 h-4" data-id="element-202" />;
      case 'group':
        return <LayersIcon className="w-4 h-4" data-id="element-203" />;
      case 'sub-group':
        return <FolderIcon className="w-4 h-4" data-id="element-204" />;
      default:
        return <BuildingIcon className="w-4 h-4" data-id="element-205" />;
    }
  };
  const getNodeColor = (type: string) => {
    switch (type) {
      case 'organization':
        return 'text-blue-600 bg-blue-50';
      case 'sub-org':
        return 'text-green-600 bg-green-50';
      case 'group':
        return 'text-purple-600 bg-purple-50';
      case 'sub-group':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = selectedNodeId === node.id;
    const colorClass = getNodeColor(node.type);
    const indentPx = depth * 20 + 12;
    return <div key={node.id} className="select-none" data-id="element-206">
        <div className={`flex items-center gap-2 py-2.5 px-3 rounded-lg cursor-pointer transition-all hover:bg-gray-100 ${isSelected ? 'bg-blue-50 border-2 border-blue-400 shadow-sm' : 'border-2 border-transparent'}`} style={{
        paddingLeft: `${indentPx}px`
      }} onClick={() => handleNodeClick(node.id, node.type)} data-id="element-207">
          {hasChildren ? <button onClick={e => {
          e.stopPropagation();
          toggleExpand(node.id);
        }} className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0" aria-label={isExpanded ? 'Collapse' : 'Expand'} data-id="element-208">
              {isExpanded ? <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-209" /> : <ChevronRightIcon className="w-4 h-4 text-gray-600" data-id="element-210" />}
            </button> : <div className="w-5 flex-shrink-0" data-id="element-211" />}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`} data-id="element-212">
            {getNodeIcon(node.type)}
          </div>
          <div className="flex-1 min-w-0" data-id="element-213">
            <div className="font-semibold text-gray-900 text-sm leading-tight" data-id="element-214">
              {node.name}
            </div>
            {node.metadata?.city && node.metadata?.state && <div className="text-xs text-gray-500 mt-0.5 leading-tight" data-id="element-215">
                {node.metadata.city}, {node.metadata.state}
              </div>}
            {node.metadata?.gradeLevel && <div className="text-xs text-gray-500 mt-0.5 leading-tight" data-id="element-216">
                {node.metadata.gradeLevel}
              </div>}
            {node.metadata?.description && !node.metadata?.gradeLevel && <div className="text-xs text-gray-500 mt-0.5 leading-tight truncate" data-id="element-217">
                {node.metadata.description}
              </div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-218">
            <div className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-bold text-gray-700 min-w-[2rem] text-center" data-id="element-219">
              {node.participantCount}
            </div>
          </div>
        </div>
        {isExpanded && hasChildren && <div className="relative" data-id="element-220">
            <div className="absolute top-0 bottom-0 w-px bg-gray-300" style={{
          left: `${indentPx + 10}px`
        }} data-id="element-221" />
            <div className="mt-1" data-id="element-222">
              {node.children.map(child => renderNode(child, depth + 1))}
            </div>
          </div>}
      </div>;
  };
  return <div className="h-full flex flex-col bg-white border-r-2 border-gray-200" data-id="element-223">
      <div className="p-4 border-b-2 border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50" data-id="element-224">
        <div className="flex items-center gap-2 mb-2" data-id="element-225">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-226">
            <BuildingIcon className="w-4 h-4 text-white" data-id="element-227" />
          </div>
          <h3 className="font-bold text-gray-900" data-id="element-228">Hierarchy</h3>
        </div>
        <p className="text-xs text-gray-600 leading-tight" data-id="element-229">
          {currentOrganization.orgType === 'K12' ? 'Schools → Grades → Classrooms' : 'Teams & Classes'}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1" data-id="element-230">
        {renderNode(hierarchyTree)}
      </div>
      <div className="p-4 border-t-2 border-gray-200 bg-gray-50" data-id="element-231">
        <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-232">
          <span data-id="element-233">Total Nodes</span>
          <span className="font-bold text-gray-900" data-id="element-234">
            {Object.keys(participantCounts).length + 1}
          </span>
        </div>
        {selectedNodeId && <div className="mt-2 text-xs text-blue-600 font-medium" data-id="element-235">
            Active Filter
          </div>}
      </div>
    </div>;
}